// timeline — etapas numa linha; um marcador brilhante anda de nó em nó, cada nó enche
// na cor de destaque com pop e o rótulo entra com blur.
import React from 'react';
import { interpolate } from 'remotion';
import type { TimelineProps } from './schema';
import { CARD, EASE_BACK, EASE_IN_OUT, EASE_OUT, MUTED, Pop, SAFE_W, alpha, fitFont, onAccent, prog, useGFrame, type GraphicComponentProps } from './common';

const W = SAFE_W + 20;
const PAD = 58;
const INNER = W - PAD * 2;
const NODE = 40;
const LINE_Y = 70; // centro da linha dentro da área

export const Timeline: React.FC<GraphicComponentProps<TimelineProps>> = ({ props, totalFrames, accent }) => {
  const f = useGFrame();
  const steps = props.steps.slice(0, 4);
  const n = steps.length;
  const colW = n <= 2 ? 300 : n === 3 ? 250 : 200;
  // nós recuados o bastante para o rótulo da ponta não vazar do cartão
  const inset = Math.max(0, colW / 2 - PAD + 14);
  const span = INNER - inset * 2;
  const xs = steps.map((_, i) => (n === 1 ? INNER / 2 : inset + (i * span) / (n - 1)));
  const first = 8;
  const step = Math.max(8, Math.min(22, Math.floor((totalFrames * 0.66 - first) / Math.max(1, n - 1))));
  const reach = steps.map((_, i) => first + i * step);
  const longest = steps.reduce((a, b) => (b.length > a.length ? b : a), '');
  const size = fitFont(longest, { weight: 700, max: 40, min: 28, maxWidth: colW - 12 });

  // marcador: segura em cada nó e desliza até o próximo
  const segT: number[] = [];
  const segX: number[] = [];
  reach.forEach((t, i) => {
    segT.push(t - (i === 0 ? 6 : 0));
    segX.push(xs[i]);
  });
  const markerX = n > 1 ? interpolate(f, segT, segX, { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: EASE_IN_OUT }) : xs[0];
  const markerIn = prog(f, 0, 6, EASE_OUT);
  const ink = onAccent(accent);

  return (
    <div style={{ ...CARD, width: W, padding: `38px ${PAD}px 40px` }}>
      <div style={{ position: 'relative', width: INNER, height: LINE_Y + 44 + size * 2.4 }}>
        {/* trilho + progresso */}
        <div style={{ position: 'absolute', left: xs[0], top: LINE_Y - 4, width: xs[n - 1] - xs[0], height: 8, borderRadius: 5, background: 'rgba(255,255,255,0.12)' }} />
        <div
          style={{
            position: 'absolute',
            left: xs[0],
            top: LINE_Y - 4,
            width: Math.max(0, markerX - xs[0]),
            height: 8,
            borderRadius: 5,
            background: `linear-gradient(90deg, ${alpha(accent, 0.55)}, ${accent})`,
            boxShadow: `0 0 16px ${alpha(accent, 0.55)}`,
          }}
        />
        {steps.map((label, i) => {
          const hit = prog(f, reach[i], reach[i] + 7, EASE_BACK);
          const lab = prog(f, reach[i] + 1, reach[i] + 8, EASE_OUT);
          const nodeScale = interpolate(hit, [0, 1], [0.7, 1]);
          return (
            <React.Fragment key={i}>
              <Pop at={reach[i]} />
              <div
                style={{
                  position: 'absolute',
                  left: xs[i] - colW / 2,
                  top: 0,
                  width: colW,
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 22,
                  letterSpacing: 2.5,
                  color: hit > 0.5 ? accent : MUTED,
                  opacity: 0.35 + 0.65 * lab,
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: xs[i] - NODE / 2,
                  top: LINE_Y - NODE / 2,
                  width: NODE,
                  height: NODE,
                  borderRadius: 999,
                  boxSizing: 'border-box',
                  border: `5px solid ${hit > 0.05 ? accent : 'rgba(255,255,255,0.28)'}`,
                  background: hit > 0.05 ? accent : '#1a1d24',
                  scale: String(nodeScale),
                  boxShadow: hit > 0.05 ? `0 0 0 ${(10 * (1 - hit)).toFixed(1)}px ${alpha(accent, 0.35)}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 30 30" width={20} height={20} style={{ overflow: 'visible' }}>
                  <path d="M 7 15.5 L 12.5 21 L 23 9.5" fill="none" stroke={ink} strokeWidth={4.5} strokeLinecap="round" strokeLinejoin="round" pathLength={1} strokeDasharray={1} strokeDashoffset={1 - prog(f, reach[i] + 3, reach[i] + 9, EASE_OUT)} />
                </svg>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: xs[i] - colW / 2,
                  top: LINE_Y + 40,
                  width: colW,
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: size,
                  lineHeight: 1.12,
                  color: '#fff',
                  opacity: lab,
                  filter: `blur(${((1 - lab) * 8).toFixed(2)}px)`,
                  translate: `0px ${((1 - lab) * 12).toFixed(1)}px`,
                }}
              >
                {label}
              </div>
            </React.Fragment>
          );
        })}
        {/* marcador */}
        <div
          style={{
            position: 'absolute',
            left: markerX - 13,
            top: LINE_Y - 13,
            width: 26,
            height: 26,
            borderRadius: 999,
            background: '#fff',
            opacity: markerIn * (1 - prog(f, reach[n - 1] + 3, reach[n - 1] + 9)),
            boxShadow: `0 0 0 6px ${alpha(accent, 0.45)}, 0 0 22px rgba(255,255,255,0.8)`,
          }}
        />
      </div>
    </div>
  );
};
